import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {DesignStateController} from '@mintdata-wsp/common/controllers/designState/DesignStateController';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {Design as DesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/Design';
import {LoadDesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/LoadDesignResponse';
import {API, fetcherSingleton} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Design, Project} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {DesignRef} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {VisualArtifact} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualArtifact';
import {BindingState, IBindingsSnapshot} from '@mintdata-wsp/common/state/ui/visualDesigner/BindingState';
import {DesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {SpreadsheetsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page, PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {ISurfaceStateSnapshot, SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {OpenDesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {BoxTreeUtil} from '@mintdata-wsp/common/util/BoxTreeUtil';
import {ViewportUtil} from '@mintdata-wsp/common/util/ViewportUtil';
import {LoadDesignResult} from '@mintdata-wsp/wsp/controllers/multiplayer/DesignSyncController';
import {
    DesignReloadedEvent,
    SnapshotRestoredEvent
} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {PositiveUnits} from '@mintdata/mint-kit/api/models/PositiveUnits';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import bind from 'bind-decorator';
import {action, autorun, comparer, IReactionDisposer, observable, reaction, runInAction} from 'mobx';
import {IDisposer} from 'mobx-utils';

interface ViewportLayoutProps
{
    height:PositiveUnits,
    width:PositiveUnits,
    top:Units,
    left:Units,
}

type OpenDesignLifecycleListener = {
    onInitialized():void
}

export class OpenDesignController extends DesignStateController implements HasEvents<OpenDesignLifecycleListener>
{
    private _designStateWatchDisposer:IDisposer = null;
    private _viewportsLayoutReactionDisposer:IReactionDisposer = null;

    private projectId:number;
    private designId:number;
    private isMultiplayerMode:boolean;

    @observable
    private _isDesignStateWatching:boolean = false;

    public init()
    {
        const {multiplayer} = controllers.ui;
        EventBus.listen(multiplayer, {
            onConnected: this._onMultiplayerConnected,
            onConnectionRestored: this._onMultiplayerConnectionRestored
        });
    }

    @bind
    private _onMultiplayerConnected()
    {
        const {multiplayer} = controllers.ui;
        multiplayer.subscribeToEvents('snapshot-restored', this._onSnapshotRestored);
        multiplayer.subscribeToEvents('design-reloaded', this._onDesignReloaded);
    }

    @bind
    private async _onMultiplayerConnectionRestored()
    {
        this._onMultiplayerConnected();

        if( this.getOpenDesign() )
            await this.reloadOpenDesign();
        else
            // TODO: fix awaiting for connection when BE is down
            await this.openDesign(this.projectId, this.designId, this.isMultiplayerMode);
    }

    @bind
    private async _onSnapshotRestored(event:SnapshotRestoredEvent)
    {
        //TODO: we should probably also show UI notification in other sessions, that didn't initiate this restore
        const {artifacts, openDesign, flow, multiplayer} = controllers.ui;
        const {projectId, designId} = openDesign.getOpenDesign().designRef;

        flow.clearFlows();
        await artifacts.ensureCloudArtifactsAreLoaded();
        await flow.ensureFlowsAreLoaded(projectId, designId);

        // TODO: instead of reconnecting, we probably can just re-initialize SyncClient to use event.currentDesign & event.currentVersion
        await multiplayer.reconnect();
        await this.reloadOpenDesign(true);

        const {designVersions} = rootState.ui.visualDesigner;

        if( designVersions.isRestoring )
        {
            runInAction(() =>
            {
                designVersions.isRestoring = false;
                designVersions.restoreVersionId = null;
            });
        }
    }

    @bind
    private async _onDesignReloaded(event:DesignReloadedEvent)
    {
        window.location.reload();
    }

    @bind
    private _initViewportsLayoutReaction():void
    {
        if( this._viewportsLayoutReactionDisposer )
        {
            this._viewportsLayoutReactionDisposer();
            this._viewportsLayoutReactionDisposer = null;
        }

        this._viewportsLayoutReactionDisposer = reaction(
            () =>
            {
                const currentLayerPage = controllers.ui.surfaceArea.getCurrentLayerPage();

                if( currentLayerPage && currentLayerPage.artboard.viewport )
                {
                    const {componentDesc} = currentLayerPage.artboard.viewport;

                    return {
                        height: componentDesc.layoutHeight,
                        width: componentDesc.layoutWidth,
                        top: componentDesc.layoutTop,
                        left: componentDesc.layoutLeft
                    };
                }
            },
            (viewportLayoutProps:ViewportLayoutProps) =>
            {
                const currentLayerPage:Page = controllers.ui.surfaceArea.getCurrentLayerPage();

                if( currentLayerPage )
                {
                    const pages:Map<PageId, Page> = controllers.ui.surfaceArea.getState().pages;

                    pages.forEach((page:Page, id:PageId) =>
                    {
                        if( id !== currentLayerPage.id )
                        {
                            const viewport:Box = page.artboard.viewport;

                            if( viewport && viewport.componentDesc )
                            {
                                viewport.componentDesc.layoutLeftProp.setPropValue(PropPath.ROOT, viewportLayoutProps.left);
                                viewport.componentDesc.layoutTopProp.setPropValue(PropPath.ROOT, viewportLayoutProps.top);
                                viewport.componentDesc.layoutWidthProp.setPropValue(PropPath.ROOT, viewportLayoutProps.width);
                                viewport.componentDesc.layoutHeightProp.setPropValue(PropPath.ROOT, viewportLayoutProps.height);
                            }
                        }
                    });
                }
            },
            {
                delay: 250,
                equals: comparer.structural
            }
        );
    }

    @action.bound
    public async reloadOpenDesign(isCheckMigration:boolean = false):Promise<void>
    {
        const {migration, spreadsheets, surfaceSelection, designSync} = controllers.ui;

        surfaceSelection.clearSelectedBoxes();

        const openDesign = this.getOpenDesign();

        const {designStateSnapshot, version} =
            await designSync.connect(openDesign.designRef, openDesign.isMultiplayerMode);

        const isDesignMigrated:boolean = isCheckMigration ? migration.migrateDesign(designStateSnapshot) : false;

        const activeSheet = spreadsheets.main.getActiveSheet();

        const ctx:IWSPMeshContext = spreadsheets.workbook.getMeshContextBySheetId(activeSheet.id);
        const meshState:MeshState = spreadsheets.mesh.getState(ctx);

        runInAction(() => openDesign.designState.surfaceSnapshot = designStateSnapshot.surface);

        spreadsheets.main.terminateRootWorkbook();

        runInAction(() =>
        {
            openDesign.designRef.version = version;

            openDesign.designState = DesignState.fromJsonDeep(designStateSnapshot);

            openDesign.designState.surfaceSnapshot = null;
        });

        spreadsheets.main.initializeRootWorkbook();

        EventBus.fire(this, 'initialized');

        const workbook:Workbook = spreadsheets.main.getRootWorkbook();
        const sheet = spreadsheets.main.findAnySheetById(activeSheet.id);

        const newActiveSheet = sheet || workbook.runtime.sheets[0];
        spreadsheets.tabs.setActiveSheet(workbook.id, newActiveSheet.id);

        if( newActiveSheet.id === activeSheet.id )
        {
            const {
                visibleWidth,
                visibleHeight,
                viewFocusActivator
            } = meshState.details;

            spreadsheets.mesh.setMeshVisibleSize(ctx, visibleWidth, visibleHeight);
            spreadsheets.mesh.setViewFocusActivator(ctx, false, viewFocusActivator);
        }

        if( activeSheet && activeSheet.isCloud )
        {
            throw new Error('Cloud sheets are not currently supported');

            // spreadsheets.cloudSheet.watchSlotUpdates(openDesign.designRef.uuid);
            //
            // await spreadsheets.cloudSheet.loadVisibleSlots(activeSheet.id);
        }

        this.onApplicationLoad();

        if( isDesignMigrated )
            designSync.syncWholeDesign();
    }

    @bind
    public async openDesign(projectId:number, designId:number, isMultiplayerMode:boolean):Promise<void>
    {
        const {
            migration,
            surfaceSelection,
            designSync,
            lx,
            guide,
            designUIState
        } = controllers.ui;

        this.projectId = projectId;
        this.designId = designId;
        this.isMultiplayerMode = isMultiplayerMode;

        const {projects} = controllers.data;
        const {mainPage} = rootState.ui;

        surfaceSelection.clearSelectedBoxes();
        guide.resetGuideState();
        designUIState.stopReaction();

        const designRef = new DesignRef(projectId, designId, null, null, null);
        const {designName, version, uuid, designStateSnapshot} = await designSync.connect(
            designRef,
            isMultiplayerMode
        );

        designRef.designName = designName;
        designRef.version = version;
        designRef.uuid = uuid;

        const openDesign = new OpenDesignState(designRef, isMultiplayerMode);

        // console.log('%c designStateSnapshot =', 'background:#0f0;color:#000;', designStateSnapshot);

        const {guideProps} = designStateSnapshot;

        if( guideProps )
            await guide.workWithLoadedGuideDesign(guideProps);

        if( !mainPage.currentProject )
        {
            const currentProject:Project = new Project();
            currentProject.id = projectId;

            mainPage.currentProject = currentProject;
        }

        if( !mainPage.currentDesigns.find((design:Design) => design.uuid === designRef.uuid) )
            mainPage.currentDesigns.push(Design.fromDesignRef(designRef));

        ViewportUtil.addViewportToVisualComponents();

        const isDesignMigrated = migration.migrateDesign(designStateSnapshot);

        // nullify surface snapshot to load it later
        const surfaceSnapshot:ISurfaceStateSnapshot = designStateSnapshot.surface;
        const bindingsSnapshot:IBindingsSnapshot = designStateSnapshot.bindings;
        designStateSnapshot.surface = null;

        this._initViewportsLayoutReaction();
        this.addHashListener();
        this.addPostMessageListener();

        // apply designer UI states first; design-specific things are ignored yet
        designUIState.loadAndApply(designId);

        runInAction(() =>
        {
            this.setOpenDesign(openDesign);

            openDesign.designState = DesignState.fromJsonDeep(designStateSnapshot);
            openDesign.designState.surfaceSnapshot = surfaceSnapshot;

            if( !openDesign.designState.spreadsheets )
                openDesign.designState.spreadsheets = SpreadsheetsState.create();
        });

        await Promise.all([
            this._initSpreadsheet(openDesign),
            this._initSurface(openDesign, surfaceSnapshot, bindingsSnapshot),
            projects.syncAllProjects()
        ]);

        // re-apply to set active page, sheet, selections, etc
        designUIState.loadAndApply(designId);

        runInAction(() =>
        {
            mainPage.currentProject = rootState.data.projects.find(({id}) => id === projectId);

            // setting redundant state so that nothing brakes
            mainPage.currentDesigns = mainPage.currentProject.designs;

            openDesign.initialized = true;

            EventBus.fire(this, 'initialized');

            setTimeout(() =>
            {
                this.restartWatchingDesignState();

                this.onApplicationLoad();

                if( isDesignMigrated )
                    designSync.syncWholeDesign();

                lx.activateLxFrame();
            });
        });

        designUIState.enableReaction();
    }

    private async _initSpreadsheet(openDesign:OpenDesignState):Promise<void>
    {
        const {projectId, designId} = openDesign.designRef;

        const {
            spreadsheets,
            flow
        } = controllers.ui;

        await flow.ensureFlowsAreLoaded(projectId, designId);

        spreadsheets.main.initializeRootWorkbook();
    }

    private async _initSurface(openDesign:OpenDesignState,
                               surfaceSnapshot:ISurfaceStateSnapshot,
                               bindingsSnapshot:IBindingsSnapshot):Promise<void>
    {
        const {designId, version} = openDesign.designRef;

        await this._resolveVisualComponents(surfaceSnapshot, designId);

        if( version > 1 )
        {
            ViewportUtil.validateViewportSettings(surfaceSnapshot);
            BoxTreeUtil.validateBoxTree(surfaceSnapshot);
        }

        runInAction(() =>
        {
            const designState = openDesign.designState;
            designState.surface = surfaceSnapshot ? SurfaceState.fromJsonDeep(surfaceSnapshot) : SurfaceState.create();
            designState.binding = bindingsSnapshot ? BindingState.fromJsonDeep(bindingsSnapshot, designState.spreadsheets, designState.surface) : BindingState.create();
            designState.surfaceSnapshot = null;
        });
    }

    private async _resolveVisualComponents(surfaceSnapshot:ISurfaceStateSnapshot,
                                           designId:number):Promise<void>
    {
        const {artifacts, flowCloud} = controllers.ui;

        await artifacts.loadVisualArtifacts();

        const visualArtifacts = artifacts.getVisualArtifacts();

        this._setupVisualArtifactsFilterState(visualArtifacts);

        await artifacts.resolveVisualComponents(surfaceSnapshot);

        return flowCloud.onDesignOpened(designId);
    }

    @bind
    public async openSampleDesign(designId:number,
                                  checkAfterOpenDesignInitialized:() => void,
                                  checkSpreadsheetVisibility:() => void,
                                  isScreenshotMode:boolean = false,
                                  projectId?:number,
                                  publicToken?:string):Promise<any>
    {
        const {migration, artifacts, surfaceSelection, spreadsheets} = controllers.ui;

        let ldr:LoadDesignResult;

        if( isScreenshotMode )
        {
            ldr = await this.loadDesignForScreenshot(projectId, designId, publicToken);
        }
        else
        {
            ldr = await this.loadSampleDesign(designId);
        }

        if( !ldr )
        {
            return;
        }

        surfaceSelection.clearSelectedBoxes();

        const designName = ldr.designName;
        const version = ldr.version;
        const uuid = ldr.uuid;
        const designRef = new DesignRef(null, designId, designName, version, uuid);
        const openDesign = new OpenDesignState(designRef);
        const designStateSnapshot = ldr.designStateSnapshot || DesignState.toJson(DesignState.create());

        ViewportUtil.addViewportToVisualComponents();

        migration.migrateDesign(designStateSnapshot);

        const surfaceSnapshot:ISurfaceStateSnapshot = designStateSnapshot.surface;
        const bindingsSnapshot:IBindingsSnapshot = designStateSnapshot.bindings;

        this._initViewportsLayoutReaction();
        this.addHashListener();
        this.addPostMessageListener();

        designStateSnapshot.surface = null;

        runInAction(() =>
        {
            this.setOpenDesign(openDesign);

            openDesign.designState = DesignState.fromJsonDeep(designStateSnapshot);
            openDesign.designState.surfaceSnapshot = surfaceSnapshot;
            spreadsheets.main.initializeRootWorkbook();
        });

        checkSpreadsheetVisibility();

        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign && sampleDesign.showSurface )
        {
            await artifacts.loadVisualArtifacts();

            const visualArtifacts = artifacts.getVisualArtifacts();

            this._setupVisualArtifactsFilterState(visualArtifacts);

            await artifacts.resolveVisualComponents(surfaceSnapshot);
        }

        runInAction(() =>
        {
            if( sampleDesign && sampleDesign.showSurface )
            {
                openDesign.designState.surface = surfaceSnapshot ? SurfaceState.fromJsonDeep(surfaceSnapshot) : null;
                openDesign.designState.surfaceSnapshot = null;
                openDesign.designState.binding = bindingsSnapshot
                    ? BindingState.fromJsonDeep(bindingsSnapshot, openDesign.designState.spreadsheets, openDesign.designState.surface)
                    : BindingState.create();
            }

            openDesign.initialized = true;

            checkAfterOpenDesignInitialized();

            setTimeout(() =>
            {
                this.onApplicationLoad();
            });
        });
    }

    @bind
    private _setupVisualArtifactsFilterState(visualArtifacts:VisualArtifact[]):void
    {
        if( !visualArtifacts )
            return;

        for( let va of visualArtifacts )
            controllers.ui.workbench.setVisibleVisualComponents(va.visualComponents);
    }

    public async loadSampleDesign(designId:number):Promise<LoadDesignResult>
    {
        return API.sample.openSampleDesign(designId)
            .then(action((response:DesignResponse | ApiError) =>
            {
                if( response instanceof ApiError )
                {
                    controllers.ui.toasts.showError(new Error(response.message));
                    return null;
                }
                else
                {
                    const cloudDesignJson = JSON.parse(response.designerState);

                    const newDesignState = cloudDesignJson.visualState || cloudDesignJson;

                    return {
                        designName: response.name,
                        uuid: response.uuid,
                        designStateSnapshot: newDesignState,
                        version: response.version
                    };
                }
            }));
    }

    public async loadDesignForScreenshot(projectId, designId, publicToken):Promise<LoadDesignResult>
    {
        fetcherSingleton.setAuthToken(decodeURIComponent(publicToken));
        return API.designs.getState(projectId, designId)
            .then(action((response:LoadDesignResponse | ApiError) =>
            {
                if( response instanceof ApiError )
                {
                    controllers.ui.toasts.showError(new Error(response.message));
                    return null;
                }
                else
                {
                    const cloudDesignJson = JSON.parse(response.designState);

                    const newDesignState = cloudDesignJson.visualState || cloudDesignJson;

                    return {
                        designName: response.designName,
                        uuid: response.uuid,
                        designStateSnapshot: newDesignState,
                        version: response.version
                    };
                }
            }));
    }

    @bind
    public takeDesignScreenshot()
    {
        const openDesign = this.getOpenDesign();

        if( openDesign )
        {
            const {projectId, designId} = openDesign.designRef;
            API.designs.takeScreenshot(projectId, designId);
        }
    }

    public restartWatchingDesignState():void
    {
        this.stopWatchingDesignState(true);
        this.startWatchingDesignState();
    }

    @action
    public startWatchingDesignState():void
    {
        this._isDesignStateWatching = true;

        if( !this._designStateWatchDisposer )
        {
            this._designStateWatchDisposer = autorun(() =>
            {
                const {openDesign, designSync} = controllers.ui;
                const designState = openDesign.getDesignState();

                if( !designState || !designState.surface )
                    return null;

                DesignState.toJson(designState);

                if( openDesign._isDesignStateWatching )
                    designSync.throttledSyncWholeDesign();
            });
        }
    }

    @action
    public stopWatchingDesignState(dispose:boolean = false):void
    {
        this._isDesignStateWatching = false;

        if( dispose && this._designStateWatchDisposer )
        {
            this._designStateWatchDisposer();
            this._designStateWatchDisposer = null;
        }
    }

    @action
    public async performDesignCleanup():Promise<void>
    {
        const {
            openDesign,
            spreadsheets,
            flowSimulation,
            flowCloud,
            flow,
            multiplayer,
            designSync,
            lx
        } = controllers.ui;

        this.removeHashListener();
        this.removePostMessageListener();

        flowCloud.disconnect();
        multiplayer.disconnect();
        designSync.disconnect();

        lx.lxCleanup();

        flow.stopAllSimulations().then(() => flowSimulation.clearAll());

        if( this._viewportsLayoutReactionDisposer )
        {
            this._viewportsLayoutReactionDisposer();
            this._viewportsLayoutReactionDisposer = null;
        }

        this.stopWatchingDesignState(true);

        await spreadsheets.main.terminateRootWorkbook();

        openDesign.setOpenDesign(null);
    }
}
